<template>
	<div>
		<template v-if="loading">
			<div class="loading">
				<Loader />
			</div>
		</template>
		<template v-else>
			<section class="section forum">
				<div class="container">
					<div class="section__row section__row--between">
						<h4 class="section__title">{{forumPost.title}}</h4>
						<button @click="$router.push('/create-post')" class="btn__link btn__link--sm">Create New Post</button>
					</div>
					<div>
						<router-link 
							:to="{name: 'forum'}"
							class="link">
							<span class="icon">
								<svg width="19" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M14.28 3.85H1.34L4.06.8a.48.48 0 1 0-.72-.64L.28 3.6a1.1 1.1 0 0 0 0 1.46L3.34 8.5a.48.48 0 0 0 .68.04c.2-.18.22-.48.04-.68L1.34 4.8h12.94a3.76 3.76 0 0 1 0 7.52H12a.48.48 0 1 0 0 .96h2.28a4.72 4.72 0 0 0 0-9.44z" fill="#000"/>
								</svg>
						</span> Back to forum
						</router-link>
						<button class="btn__plain" @click="reportForumTopic">
                            report
                        </button>
					</div>
				</div>
				<div class="container">
					<div class="forum__wrapper">
						<main class="forum__main">
							<div class="forum__body">
								<div class="forum__thread">
									<ul class="">
										<li class="forum__post item__row">
											<img class="post__img" :src="forumPost.user.avatar" alt="">
											<div class="item__body">
												<div class="">
													<span class="title bold">{{`${forumPost.user.fname} ${forumPost.user.lname}`}}</span>
													<span class="grey">{{ forumPost.created_at | date }}</span>
												</div>
												<p class="forum__text main">{{forumPost.body}}</p>
											</div>
										</li>
										<li class="forum__post item__row">
											<img class="post__img" :src="forumPost.user.avatar" alt="">
											<form
												@submit.prevent="createThread" 
												class="forum__post__form item__body" action="">
												<label
													class="label--block" 
													for="forum-thread">
													<textarea 
														class="input input--lg"
														:class="[{ 'is-invalid': errors.body}]" 
														name="" 
														id="forum-thread" 
														cols="50" 
														rows="3"
														@keydown="clearErrors($event.target.name)"
														v-model="itemData.body"
														placeholder="Type here to reply..."></textarea>
												</label>
												<div class="btn-group">
												<button
													:v-bind="{'disabled': loading || !itemData.body}"
													type="submit"
													@click.prevent="createThread" 
													class="btn__primary btn__primary--alt">Post Comment</button>
													<Loader v-if="loading"/>
												</div>
											</form>
										</li>
									</ul>
									<h5 v-if="threads.length" class="bold">{{ threads.length }} comment<template v-if="threads.length > 1">s</template>
									</h5>
									<ul class="forum__thread">
										<template v-if="loadingThreads">
											<div class="loading">
												<Loader />
											</div>
										</template>
										<template v-else>
											<template v-if="threadsLoaded && threads.length">
												<li 
													v-for="thread in threads" 
													:key="thread.id">
													<div class="forum__post item__row thread">
														<img class="post__img post__img--sm" :src="thread.user.avatar" alt="">
														<div class="item__body">
															<p class="forum__text"><span class="bold">{{`${thread.user.fname} ${thread.user.lname}`}}</span> 
															{{thread.body}}</p>
															<div class="item__meta">
																<span>{{ thread.created_at | date}}</span>
                                                                <button class="btn__plain btn__plain--sm" @click="reportForumComment(thread)">Report</button>
															</div>
														</div>
													</div>
												</li>
											</template>
											<template 	
												v-else-if="threadsLoaded">
												No Comments
											</template>
											<template v-else>
												Error loading comments
											</template>
										</template>
									</ul>
									<Pagination 
										v-if="total > limit"
										:options="{ limit, offset, total, baseUrl }"/>
								</div>
							</div>
						</main>
						<aside class="forum__right">         
							<section class="section__side">
								<h3 class="forum__title">Recent Posts</h3>
								<h6 class="forum__title__meta">Be the first to comment on these new discussions and articles.</h6>
								<div class="forum__section">
									<template v-if="forumTopics.length">
										<div
											v-for="topic in forumTopics.slice(0, 5)"
											:key="topic.id"  
											class="item">
											<div class="section__row">
												<img class="post__img post__img--sm" :src="topic.user.avatar" alt="">
												<span class="body">
													<router-link
														class="item__link bold"
														:to="{ name: 'user-profile', params: { id: topic.user.id } }">
														{{`${topic.user.fname} ${topic.user.lname}`}}
													</router-link>
												</span>
												<span 	
													class="grey">posted</span>
											</div>
											<router-link
												@click.native="setForumPost(topic)"
												:to="{
													name: 'forum-thread',
													params: {id: topic.id}
												}">
												{{topic.body}}
											</router-link>
											<div class="body__meta">
											</div>
										</div>
									</template>
									<template v-else>
										No Posts yet
									</template>
								</div>
							</section>
						</aside>
					</div>
				</div>  
			</section>
		</template>
	</div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import Loader from '../../components/Loader';
import Pagination from '../../components/Pagination';
import form from '../../mixins/form';
import forum from '../../utils/validations/forum';
import { getForumTopic, createForumThread, reportForumTopic, reportForumComment } from '../../utils/api/forum';
import { success, fail } from '../../utils/toast';

export default {
	name: 'ForumThread',
	mixins: [form],
	data() {
		return {
			threads: [],
			loadingThreads: false,
			threadsLoaded: true,
			itemData: {
				body: ''
			},
			errors: {},
			loading: false,
			limit: 6,
			offset: 0,
			total: 0,
			baseUrl: '',
			isTopicOwner: false,
			isThreadOwner: false
		};
	},
	created() { 
		this.fetchForumTopic();
		this.limit = this.$route.query.limit ? this.$route.query.limit : this.limit;
		this.offset = this.$route.query.offset ? this.$route.query.offset : this.offset;
		this.baseUrl = `forum/${this.forumPost.id}/thread`;
		if (this.$session.get('category') === 'employer') {
			this.$router.push('/jobs');
		}
	},
	computed: {	
		...mapGetters({
			forumTopics: 'fetchForumTopics',
			forumPost: 'getForumPost',
			loggedUser: 'getLoggedUser'
		})
	},
	methods: {
		...mapMutations([
			'saveForumPost'    
		]),
		setForumPost(forumPost) {
			this.saveForumPost(forumPost);
		},
		fetchForumTopic() {
			this.loadingThreads = true;
			getForumTopic(this.$route.params.id)
				.then((response) => {
					if (response.status === 200) {
						this.threadsLoaded = true;
						this.forum = response.data.data;
						this.threads = response.data.data.thread;
						this.threads.forEach((thread) => {
							if (thread.user_id === this.loggedUser.id) {
								this.isThreadOwner = true;
								return true;
							}
							return false;
						});
						if (this.forum.user_id === this.loggedUser.id) {
							this.isTopicOwner = true;
							return true;
						}
					}
				})
				.catch((error) => {
					this.threadsLoaded = false;
					return error;
				})
				.finally(() => {
					this.loadingThreads = false;
				});
			return true;
		},
		createThread() {
			this.loading = true;
			this.validate(this.itemData, forum.thread);
			if (Object.keys(this.issues).length > 1) {
				this.errors = this.issues;
				return false;
			}
			createForumThread(
				this.forumPost.id, 
				{
					...this.itemData,
					forum_topic_id: this.forumPost.id,
					user_id: this.loggedUser.id
				}
			)
				.then((response) => {
					if (response.status === 201) {
						this.loading = false;
						this.threads.push({ ...response.data, user: this.loggedUser });
						this.threadsLoaded = true;
						this.clearFormField();
						return true;
					}
					return false;
				})
				.catch((error) => {
					this.loading = false;
					this.threadsLoaded = false;
					return error;
				});
			return true;
		},
		reportForumTopic() {
			const data = {
				user_id: this.loggedUser.id,
				topic_id: this.$route.params.id
			};
			reportForumTopic(data)
				.then((response) => {
					if (response.status === 200) {
						success({
							title: 'Success! Forum topic has been reported!'
						});
						return true;
					}
					return false;
				})
				.catch((error) => {
					fail({
						title: 'Oops! Something went wrong',
						text: error.data
					});
					return false;
				});
			return true;
		},
		reportForumComment(thread) {
			const data = {
				user_id: this.loggedUser.id,
				forum_topic_id: this.$route.params.id,
				comment_id: thread.id
			};
			reportForumComment(data)
				.then((response) => {
					if (response.status === 200) {
						success({
							title: 'Success! Forum comment has been reported!'
						});
						return true;
					}
					return false;
				})
				.catch((error) => {
					fail({
						title: 'Oops! Something went wrong',
						text: error.data
					});
					return false;
				});
			return true;
		}
	},
	watch: {
		forumPost() {
			window.location.reload();
		}
	},
	components: {
		Pagination, Loader
	},
	filters: {
		date(value) {
			return moment(value).fromNow();
		}
	}
};
</script>

<style lang="scss" scoped>
.main {
	background: #fff;
}
button:disabled {
	background: lightgrey;
	color: #fff;
	&:hover {
		cursor: not-allowed;
	}
}
.loading {
	min-height: 100px;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
